import { useEffect } from 'react'

import { ActionCenterTaskType } from '@domain'
import { Box, Button, Drawer, Stack, Typography } from '@mui/material'
import { ContractSummaryStatus } from '@services/contractLoading.types'
import { StringParam, useQueryParam } from 'use-query-params'

import Contract from './Contract/Contract'
import ContractList from './ContractList/ContractList'

type Props = {
	onClose: () => void
}

export default function ContractLoading({ onClose }: Props) {
	const [activeTask, setActiveTask] = useQueryParam('active-task', StringParam)
	const [statusFilter, setStatusFilter] = useQueryParam('contracts', StringParam)
	const [selectedId, setSelectedId] = useQueryParam('contract', StringParam)

	const hasSelectedContract = !!selectedId
	const contractLoadingCompleted =
		statusFilter === ContractSummaryStatus.PENDING && !hasSelectedContract

	useEffect(() => {
		if (activeTask !== ActionCenterTaskType.CONTRACT_LOADING) return
		if (statusFilter === undefined || statusFilter === null) {
			setStatusFilter(ContractSummaryStatus.PENDING, 'replaceIn')
		}
	}, [activeTask, setStatusFilter, statusFilter])

	const handleUpdateContract = (id: Id = null) => {
		if (id) {
			setSelectedId(id.toString(), 'replaceIn')
			return
		}

		setSelectedId(null)
	}

	const handleSelect = (id: Id) => {
		if (!id) {
			setSelectedId(null, 'replaceIn')
			return
		}

		setSelectedId(id.toString(), 'replaceIn')
	}

	const handleClose = () => {
		setStatusFilter(undefined)
		setActiveTask(undefined)
		setSelectedId(undefined)
		onClose()
	}

	if (activeTask !== ActionCenterTaskType.CONTRACT_LOADING) return null

	return (
		<Drawer
			open={activeTask === ActionCenterTaskType.CONTRACT_LOADING}
			variant="temporary"
			anchor="right"
			onClose={handleClose}
			data-testid="contract-loading-drawer"
			sx={{ '& .MuiDrawer-paper': { width: '95vw' }, zIndex: (theme) => theme.zIndex.modal + 1 }}
		>
			<Stack direction="row" height="100%" spacing={2}>
				<ContractList
					onSelect={handleSelect}
					onChangeStatus={(status) => setStatusFilter(status)}
					onClose={handleClose}
				/>
				{hasSelectedContract && (
					<Contract contractId={Number(selectedId)} onUpdateContract={handleUpdateContract} />
				)}
				{contractLoadingCompleted && <ContractLoadingCompleted onClose={handleClose} />}
			</Stack>
		</Drawer>
	)
}

function ContractLoadingCompleted({ onClose }: { onClose: () => void }) {
	return (
		<Stack p={2} width="100%" data-testid="contract-loading-completed">
			<Typography variant="h6">Selected Price Contract</Typography>
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Stack alignItems="center" spacing={2}>
					<Typography variant="body1" color="text.secondary">
						All Contract Loading tasks have been reviewed! Great work!
					</Typography>
					<Button
						variant="outlined"
						color="secondary"
						onClick={onClose}
						data-testid="contract-loading-close-button"
					>
						Close
					</Button>
				</Stack>
			</Box>
		</Stack>
	)
}
